import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Award, Sprout, Trash2, Globe, Plus, Loader2, Heart, Flag } from 'lucide-react';
import { api } from '../lib/api';
import { findPhotoUrl } from '../lib/wildFinds';
import { toast } from 'sonner';
import LogFindModal from '../components/LogFindModal';
import FindPhoto from '../components/FindPhoto';

const CATEGORIES = [
    { id: 'garden', label: 'Garden', emoji: '🪴' },
    { id: 'foraging', label: 'Foraging challenges', emoji: '🌿' },
    { id: 'community', label: 'Community', emoji: '🤝' },
];

export default function Achievements() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState('badges');
    const [finds, setFinds] = useState([]);
    const [community, setCommunity] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [logOpen, setLogOpen] = useState(false);
    const [reportId, setReportId] = useState(null);
    const [reportReason, setReportReason] = useState('');

    const loadAch = useCallback(() => {
        api.get('/achievements').then((r) => setData(r.data)).catch(() => setData(null));
    }, []);
    useEffect(() => { loadAch(); }, [loadAch]);

    const loadFinds = useCallback(async () => {
        setLoadingList(true);
        try {
            const [mine, comm] = await Promise.all([
                api.get('/finds'),
                api.get('/finds/community'),
            ]);
            setFinds(mine.data?.items || []);
            setCommunity(comm.data?.items || []);
        } catch { /* ignore */ } finally { setLoadingList(false); }
    }, []);
    useEffect(() => { if (tab !== 'badges') loadFinds(); }, [tab, loadFinds]);

    const onLogged = () => { loadAch(); loadFinds(); };

    const deleteFind = async (id) => {
        try { await api.delete(`/finds/${id}`); setFinds((f) => f.filter((x) => x.id !== id)); loadAch(); toast.success('Find removed'); }
        catch { toast.error('Could not remove'); }
    };

    const likeFind = async (id) => {
        try {
            const r = await api.post(`/finds/${id}/like`);
            setCommunity((list) => list.map((x) => x.id === id ? { ...x, liked: r.data.liked, like_count: r.data.like_count } : x));
        } catch { toast.error('Could not like'); }
    };

    const submitReport = async () => {
        const id = reportId;
        try {
            const r = await api.post(`/finds/${id}/report`, { reason: reportReason });
            setCommunity((list) => list.filter((x) => x.id !== id));
            toast.success(r.data?.hidden ? 'Reported & hidden — thank you' : 'Reported — thank you');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not report');
        } finally { setReportId(null); setReportReason(''); }
    };

    const pct = data ? Math.round((data.unlocked_count / data.total_count) * 100) : 0;

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="achievements-page">
            <button data-testid="achievements-back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/dashboard'))} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Tasks &amp; achievements</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-4">Your badges &amp; finds</h1>

            {data && (
                <div className="bg-gradient-to-br from-brand-green to-brand-greenDark text-white rounded-3xl p-5 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest font-semibold text-brand-bg/70">Progress</div>
                            <div className="font-serif text-2xl mt-1">{data.unlocked_count} / {data.total_count} unlocked</div>
                        </div>
                        <Award size={32} className="text-brand-bg/80" strokeWidth={1.5} />
                    </div>
                    <div className="mt-3 h-2 bg-white/20 rounded-full overflow-hidden">
                        <div className="h-full bg-brand-ochre transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex gap-4 mt-4 text-xs text-brand-bg/85">
                        <span>🌿 {data.stats.wild_finds} wild</span>
                        <span>🍄 {data.stats.mushroom_finds} mushrooms</span>
                        <span>🤝 {data.stats.shared_finds} shared</span>
                    </div>
                </div>
            )}

            <button data-testid="open-logfind" onClick={() => setLogOpen(true)}
                className="w-full mb-3 inline-flex items-center justify-center gap-2 bg-brand-ochre text-white py-3 rounded-full text-sm font-medium hover:opacity-95 transition-opacity">
                <Plus size={16} /> Log a find (wild plant or mushroom)
            </button>

            <button data-testid="open-leaderboard" onClick={() => navigate('/leaderboard')}
                className="w-full mb-6 inline-flex items-center justify-center gap-2 bg-brand-card border border-brand-border text-brand-ink py-3 rounded-full text-sm font-medium hover:border-brand-green transition-colors">
                <Award size={16} className="text-amber-400" /> Leaderboard — top gardeners
            </button>

            <div className="flex gap-2 mb-6">
                {[['badges', 'Badges'], ['mine', 'My finds'], ['community', 'Community']].map(([id, label]) => (
                    <button key={id} data-testid={`ach-tab-${id}`} onClick={() => setTab(id)}
                        className={`flex-1 py-2.5 rounded-full text-sm transition-colors ${tab === id ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink'}`}>
                        {label}
                    </button>
                ))}
            </div>

            {tab === 'badges' && data && (
                <div className="space-y-7">
                    {CATEGORIES.map((cat) => {
                        const list = data.items.filter((a) => a.category === cat.id);
                        if (!list.length) return null;
                        return (
                            <div key={cat.id}>
                                <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3">{cat.emoji} {cat.label}</h2>
                                <div className="space-y-2 stagger">
                                    {list.map((a) => (
                                        <div data-testid={`achievement-${a.id}`} key={a.id} className={`flex items-center gap-3 bg-brand-card border border-brand-border rounded-2xl p-4 ${a.unlocked ? '' : 'opacity-60'}`}>
                                            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-xl ${a.unlocked ? 'bg-brand-ochre/20' : 'bg-brand-bg2'}`}>{a.icon}</div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-serif text-base text-brand-ink">{a.title}</div>
                                                <div className="text-xs text-brand-ink3">{a.desc}</div>
                                                <div className="mt-1.5 h-1 bg-brand-bg2 rounded-full overflow-hidden">
                                                    <div className={`h-full ${a.unlocked ? 'bg-brand-green' : 'bg-brand-sage'}`} style={{ width: `${(a.progress / a.target) * 100}%` }} />
                                                </div>
                                            </div>
                                            {a.unlocked && <Award size={16} className="text-brand-ochre flex-shrink-0" strokeWidth={1.8} />}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {tab === 'mine' && (
                loadingList ? <Spinner /> :
                finds.length === 0 ? <Empty text="No finds yet. Tap “Log a find” to start your foraging journal." /> :
                <div className="space-y-3">{finds.map((f) => <FindCard key={f.id} f={f} onDelete={() => deleteFind(f.id)} />)}</div>
            )}

            {tab === 'community' && (
                loadingList ? <Spinner /> :
                community.length === 0 ? <Empty text="No shared finds yet — be the first to share one!" /> :
                <div className="space-y-3">{community.map((f) => <FindCard key={f.id} f={f} community onLike={() => likeFind(f.id)} onReport={() => setReportId(f.id)} />)}</div>
            )}

            <LogFindModal open={logOpen} onClose={() => setLogOpen(false)} onLogged={onLogged} />

            {reportId && (
                <div className="fixed inset-0 z-[78] bg-brand-ink/45 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setReportId(null)} data-testid="report-modal">
                    <div className="bg-brand-bg w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl border border-brand-border p-6 animate-fade-up" onClick={(e) => e.stopPropagation()}>
                        <div className="w-12 h-12 rounded-2xl bg-brand-terracotta/12 text-brand-terracotta flex items-center justify-center mb-3"><Flag size={22} /></div>
                        <h3 className="font-serif text-xl text-brand-ink">Report this find</h3>
                        <p className="text-sm text-brand-ink2 mt-1">Tell us what's wrong (optional). Finds with multiple reports are automatically hidden and reviewed.</p>
                        <textarea data-testid="report-reason" value={reportReason} onChange={(e) => setReportReason(e.target.value)} rows={3} placeholder="Reason (optional)"
                            className="w-full mt-3 bg-brand-card border border-brand-border rounded-xl px-3 py-2.5 text-sm text-brand-ink placeholder:text-brand-ink3 focus:outline-none focus:border-brand-green resize-y" />
                        <div className="flex gap-2 mt-4">
                            <button onClick={() => setReportId(null)} className="flex-1 py-3 rounded-full text-sm bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2">Cancel</button>
                            <button data-testid="report-submit" onClick={submitReport} className="flex-1 py-3 rounded-full text-sm bg-brand-terracotta text-white hover:opacity-90">Report</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function FindCard({ f, onDelete, community, onLike, onReport }) {
    const url = findPhotoUrl(f);
    return (
        <div className="bg-brand-card border border-brand-border rounded-3xl overflow-hidden" data-testid={`find-${f.id}`}>
            {url && <FindPhoto find={f} publicView={!!community} alt={f.species_name || 'Find photo'} loading="lazy" className="w-full h-48 object-cover" />}
            <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="text-lg">{f.kind === 'mushroom' ? '🍄' : '🌿'}</span>
                            <h3 className="font-serif text-base text-brand-ink truncate">{f.species_name}</h3>
                        </div>
                        {f.note && <p className="text-xs text-brand-ink3 mt-1">{f.note}</p>}
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-brand-ink3">
                            {community && <span className="inline-flex items-center gap-1">{f.author}</span>}
                            {f.shared && <span className="inline-flex items-center gap-1 text-brand-green"><Globe size={10} /> Shared</span>}
                            <span>{(f.created_at || '').slice(0, 10)}</span>
                            {community && onReport && (
                                <button data-testid={`find-report-${f.id}`} onClick={onReport} className="inline-flex items-center gap-1 hover:text-brand-terracotta transition-colors" title="Report">
                                    <Flag size={11} /> Report
                                </button>
                            )}
                        </div>
                    </div>
                    {community ? (
                        <button data-testid={`find-like-${f.id}`} onClick={onLike}
                            className={`inline-flex items-center gap-1 text-sm flex-shrink-0 transition-colors ${f.liked ? 'text-brand-terracotta' : 'text-brand-ink3 hover:text-brand-terracotta'}`}>
                            <Heart size={16} fill={f.liked ? 'currentColor' : 'none'} /> {f.like_count || 0}
                        </button>
                    ) : onDelete && (
                        <button data-testid={`find-delete-${f.id}`} onClick={onDelete} className="p-2 text-brand-ink3 hover:text-brand-terracotta rounded-full flex-shrink-0"><Trash2 size={15} /></button>
                    )}
                </div>
            </div>
        </div>
    );
}

const Spinner = () => <div className="text-center py-16"><Loader2 className="animate-spin inline text-brand-ink3" /></div>;
const Empty = ({ text }) => (
    <div className="text-center py-16 px-6">
        <Sprout size={32} className="text-brand-sage mx-auto mb-3" strokeWidth={1.5} />
        <p className="text-sm text-brand-ink3">{text}</p>
    </div>
);
